// Header-based column mapping for the CRM workbook's Organizations and
// Opportunities tabs. Shared by the browser (CSV export) and the Netlify
// Functions (Sheets API), so both read and write the same columns.
//
// Columns are never fixed by position or letter: William inserts columns (the
// Instagram Profile column pushed Email, Phone, City, State and Notes one to
// the right), so every read and write resolves the live header row first.
// Aliases are explicit, not fuzzy; a header that matches two columns, or a
// needed header that is missing, is an error instead of a guess.

/** Row 4 holds the headers; data starts on row 5. */
export const CRM_HEADER_ROW = 4
export const CRM_DATA_START_ROW = 5

export const ORGANIZATION_COLUMNS = Object.freeze({
  priority: ['Priority'],
  name: ['Name'],
  type: ['Type'],
  accountManager: ['Acct. Manager', 'Account Manager'],
  website: ['Website'],
  facebook: ['FB Profile', 'Facebook Profile'],
  instagram: ['Instagram Profile'],
  // "Linkedin Prof" is how the narrow live column reads; accept it if typed that way.
  linkedin: ['LinkedIn Profile', 'LinkedIn Prof'],
  email: ['Email'],
  phone: ['Phone'],
  city: ['City'],
  state: ['State'],
  notes: ['Notes'],
})

export const OPPORTUNITY_COLUMNS = Object.freeze({
  priority: ['Priority'],
  name: ['Name'],
  dealOwner: ['Deal Owner'],
  organization: ['Organization'],
  status: ['Status'],
  stage: ['Stage'],
  lossReason: ['Loss Reason'],
  source: ['Source'],
  value: ['Value'],
  probability: ['Probability'],
  expectedClose: ['Exp. Close'],
  notes: ['Notes'],
  dateCollected: ['Date Collected', 'Collected Date'],
})

export class SheetSchemaError extends Error {
  constructor(sheet, { missing = [], ambiguous = [] }, definition = {}) {
    const label = (field) => `"${definition[field]?.[0] ?? field}"`
    const parts = []
    if (missing.length) parts.push(`no ${missing.map(label).join(', ')} column`)
    if (ambiguous.length) parts.push(`more than one ${ambiguous.map(label).join(', ')} column`)
    super(`The ${sheet} sheet headers are not usable: ${parts.join('; ')}.`)
    this.name = 'SheetSchemaError'
    this.sheet = sheet
    this.missing = missing
    this.ambiguous = ambiguous
  }
}

/** Case, whitespace and punctuation never change a header's meaning. */
export function normalizeHeader(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '')
}

/**
 * Maps each logical field to its 0-based index in `headerRow`. Fields whose
 * header is absent are left out unless listed in `required`, which throws.
 * A header that appears twice always throws, because either copy could be the
 * one the user means.
 */
export function resolveColumns(headerRow, definition, { sheet = 'CRM', required = [] } = {}) {
  const lookup = new Map()
  for (const [field, aliases] of Object.entries(definition)) {
    for (const alias of aliases) lookup.set(normalizeHeader(alias), field)
  }

  const columns = {}
  const ambiguous = new Set()
  ;(Array.isArray(headerRow) ? headerRow : []).forEach((cell, index) => {
    const field = lookup.get(normalizeHeader(cell))
    if (!field) return
    if (field in columns) ambiguous.add(field)
    else columns[field] = index
  })

  const missing = required.filter((field) => !(field in columns))
  if (missing.length || ambiguous.size) {
    throw new SheetSchemaError(sheet, { missing, ambiguous: [...ambiguous] }, definition)
  }
  return columns
}

/** 0-based column index -> A1 column letters (0 -> A, 26 -> AA). */
export function columnLetter(index) {
  let letters = ''
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters
  }
  return letters
}

/** Reads one logical field from a row, '' when its column does not exist. */
export function cellFor(cells, columns, field) {
  const index = columns[field]
  return index === undefined ? '' : cells?.[index] ?? ''
}

// ponytail: A:AZ covers 52 columns; widen if the tabs ever grow past AZ.
export const ORGANIZATIONS_RANGE = `Organizations!A${CRM_HEADER_ROW}:AZ`
export const OPPORTUNITIES_RANGE = `Opportunities!A${CRM_HEADER_ROW}:AZ`
